import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

export type SmsProvider = 'TWILIO' | 'DIALPAD';
export type SmsProviderStatus = 'UNVERIFIED' | 'ACTIVE' | 'REQUIRES_SENDER_ID' | 'DISABLED' | 'ERROR';
export type SmsWebhookStatus = 'PENDING' | 'CONFIGURED' | 'ERROR';

export interface SmsNumber {
  id: string;
  sms_provider_account_id: string;
  provider_number_sid: string | null;
  e164: string;
  friendly_name: string | null;
  country_code: string;
  webhook_configured_at: string | null;
  webhook_status: SmsWebhookStatus;
  assigned_user_id: string | null;
  last_webhook_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface SmsProviderAccount {
  id: string;
  org_id: string;
  user_id: string | null;
  label: string;
  provider: SmsProvider;
  status: SmsProviderStatus;
  requires_sender_id_au: boolean;
  daily_message_cap: number;
  monthly_spend_cap_cents: number | null;
  last_error: string | null;
  last_verified_at: string | null;
  created_at: string;
  updated_at: string;
  numbers: SmsNumber[];
  /** A Twilio account connected without its Auth Token: its replies and delivery updates are refused (JT1-1860). */
  needs_auth_token?: boolean;
}

/* ── Per-provider credential payload shapes ──────────────────────────────── */

export interface TwilioCredentials {
  account_sid: string;
  api_key_sid: string;
  api_key_secret: string;
  /** Twilio signs its webhooks with it, so replies and delivery updates need it (JT1-1860). */
  auth_token: string;
}

/** Discriminated union of every supported provider's credential payload.
 *  Adding a new provider = adding a branch here and a corresponding form
 *  variant in the AddSmsAccountModal. Dialpad has none: it uses the agency's
 *  Dialpad connection in JobTetris (JT1-1856). */
export type CreateSmsAccountPayload = {
  label: string;
  user_id?: string | null;
  daily_message_cap?: number;
  monthly_spend_cap_cents?: number | null;
} & (
  | {
      provider: 'TWILIO';
      credentials: TwilioCredentials;
      /** Are these numbers registered for Australian sending? Until yes, Australian texts wait. */
      au_sender_registered: boolean;
    }
  | { provider: 'DIALPAD' }
);

/** Whether Dialpad from JobTetris can be turned on, and how many people can text through it. */
export interface DialpadAvailability {
  available: boolean;
  senders: number;
  reason: string | null;
}

export interface UpdateSmsAccountPayload {
  label?: string;
  user_id?: string | null;
  daily_message_cap?: number;
  monthly_spend_cap_cents?: number | null;
  status?: 'ACTIVE' | 'DISABLED';
}

/* ── Hooks ───────────────────────────────────────────────────────────────── */

export function useSmsAccounts() {
  return useQuery<{ data: SmsProviderAccount[] }>({
    queryKey: ['sms-accounts'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: SmsProviderAccount[] }>('/sms-accounts');
      return data;
    },
    refetchInterval: (q) => {
      const list = q.state.data?.data;
      return list?.some((a) => a.status === 'UNVERIFIED') ? 3000 : false;
    },
  });
}

export function useDialpadAvailability(enabled = true) {
  return useQuery<{ data: DialpadAvailability }>({
    queryKey: ['sms-accounts', 'dialpad-availability'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: DialpadAvailability }>('/sms-accounts/dialpad/availability');
      return data;
    },
    enabled,
  });
}

export function useCreateSmsAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateSmsAccountPayload) => {
      const { data } = await apiClient.post<{ data: SmsProviderAccount }>('/sms-accounts', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-accounts'] }),
  });
}

export function useUpdateSmsAccount(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateSmsAccountPayload) => {
      const { data } = await apiClient.patch<{ data: SmsProviderAccount }>(`/sms-accounts/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-accounts'] }),
  });
}

export function useRetestSmsAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<{ data: SmsProviderAccount }>(`/sms-accounts/${id}/retest`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-accounts'] }),
  });
}

/** Adds the Auth Token to a Twilio account connected without one (JT1-1860). */
export function useSetTwilioAuthToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, auth_token }: { id: string; auth_token: string }) => {
      const { data } = await apiClient.put<{ data: SmsProviderAccount }>(`/sms-accounts/${id}/auth-token`, { auth_token });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-accounts'] }),
  });
}

export function useConfirmAuSenderId() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<{ data: SmsProviderAccount }>(`/sms-accounts/${id}/confirm-au-sender-id`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-accounts'] }),
  });
}

export function useDeleteSmsAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/sms-accounts/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-accounts'] }),
  });
}

/**
 * Link a number/sender on an account to an Engage user (or unlink with
 * assigned_user_id=null). Admin-only server-side. For Dialpad this maps a
 * Dialpad user's number to the Engage recruiter whose automations send from it.
 */
export function useAssignSmsNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { accountId: string; numberId: string; assigned_user_id: string | null }) => {
      const { data } = await apiClient.patch<{ data: SmsProviderAccount }>(
        `/sms-accounts/${args.accountId}/numbers/${args.numberId}/assignment`,
        { assigned_user_id: args.assigned_user_id }
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-accounts'] }),
  });
}
